import { createWithEqualityFn } from 'zustand/traditional';

import { UserPreferences } from '@common-types';

import { baseService } from '@services/base.service';

import useUserPreferencesStore from '@store/UserPreferencesStore';
import useNotificationStore from '@store/NotificationStore';

import { ResultFilters } from '@store/types/filters';

import { INITIAL_VALUE, SortByValues } from '@pages/Scholarships/components/SortBy/SortBy';

type ScholarshipListFilters = ResultFilters;

interface FavoriteScholarshipsStore {
  filters: ScholarshipListFilters;
  sortByValues: SortByValues;

  toggleFavorite: (scholarshipId: number, status: boolean) => void;
  setSortByFields: (sortBy: SortByValues) => void;
  setSearchFilters: (searchFilters: ScholarshipListFilters) => void;
  removeSearchFilter: (searchFilter: keyof ScholarshipListFilters) => void;
}

const initialState = {
  sortByValues: INITIAL_VALUE,
  filters: {} as ScholarshipListFilters,
};

export const useFavoriteScholarshipsStore = createWithEqualityFn<FavoriteScholarshipsStore>()(
  (set, get) => ({
    ...initialState,

    toggleFavorite: async (scholarshipId: number, status: boolean) => {
      if (status) {
        const userPreferences = await baseService.deleteAsync<UserPreferences>(
          `/user/favorites/scholarships/${scholarshipId}`,
        );

        useNotificationStore.getState().notify({
          message: `Removed from Favorites`,
          severity: 'info',
        });

        useUserPreferencesStore.getState().setUserPreferences(userPreferences);
      } else {
        const userPreferences = await baseService.postAsync<UserPreferences, unknown>('/user/favorites/scholarships', {
          scholarshipId,
        });

        useNotificationStore.getState().notify({
          message: `Saved to Favorites`,
          severity: 'info',
        });

        useUserPreferencesStore.getState().setUserPreferences(userPreferences);
      }
    },

    setSortByFields: (sortByValues: SortByValues) => {
      set({
        sortByValues,
      });
    },

    setSearchFilters: (searchFilters: ScholarshipListFilters) => {
      set({
        filters: { ...get().filters, ...searchFilters },
      });
    },

    removeSearchFilter: (searchFilter: keyof ScholarshipListFilters) => {
      const filters = { ...get().filters };

      delete filters[searchFilter];

      set({
        filters,
      });
    },
  }),
  Object.is,
);
